// src/components/Pagination.tsx
import React from "react";
import {
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

interface PaginationProps {
    pageIndex: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    pageSizeOptions?: number[];
    onPageChange: (pageIndex: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
                                                          pageIndex,
                                                          pageSize,
                                                          totalItems,
                                                          totalPages,
                                                          pageSizeOptions = [10, 25, 50, 100],
                                                          onPageChange,
                                                          onPageSizeChange,
                                                      }) => {
    const startItem = pageIndex * pageSize + 1;
    const endItem = Math.min(startItem + pageSize - 1, totalItems);

    const getPagesToDisplay = () => {
        const pages: number[] = [];

        for (let i = 1; i <= totalPages; i++) {
            if (
                i === 1 ||
                i === totalPages ||
                Math.abs(i - (pageIndex + 1)) <= 1
            ) {
                pages.push(i);
            }
        }

        return pages;
    };

    const goToPage = (index: number) => {
        onPageChange(Math.max(0, Math.min(index, totalPages - 1)));
    };

    return (
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600 w-full sm:w-auto text-center sm:text-left">
                Showing <span className="font-medium">{startItem}</span> -{" "}
                <span className="font-medium">{endItem}</span> of{" "}
                <span className="font-medium">{totalItems}</span> results
            </div>

            {/* Page size dropdown */}
            {onPageSizeChange && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <label htmlFor="page-size">Rows per page:</label>
                    <select
                        id="page-size"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        className="border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 hover:cursor-pointer"
                    >
                        {pageSizeOptions.map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Desktop pagination controls */}
            <div className="hidden sm:flex items-center gap-1">
                <button
                    onClick={() => goToPage(0)}
                    disabled={pageIndex === 0}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                    onClick={() => goToPage(pageIndex - 1)}
                    disabled={pageIndex === 0}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-1">
                    {getPagesToDisplay().map((page, idx, arr) => (
                        <React.Fragment key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                                <span className="px-2 text-gray-400">...</span>
                            )}
                            <button
                                onClick={() => goToPage(page - 1)}
                                className={`px-3 py-1 rounded-md text-sm hover:cursor-pointer ${
                                    pageIndex + 1 === page
                                        ? "bg-blue-600 text-white shadow-sm"
                                        : "text-gray-600 hover:bg-gray-100"
                                }`}
                            >
                                {page}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                <button
                    onClick={() => goToPage(pageIndex + 1)}
                    disabled={pageIndex >= totalPages - 1}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
                <button
                    onClick={() => goToPage(totalPages - 1)}
                    disabled={pageIndex >= totalPages - 1}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronsRight className="h-4 w-4" />
                </button>
            </div>

            {/* Mobile controls */}
            <div className="flex sm:hidden items-center justify-between w-full">
                <button
                    onClick={() => goToPage(pageIndex - 1)}
                    disabled={pageIndex === 0}
                    className="p-2 text-gray-500 bg-white border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>

                <span className="text-sm">
                  Page {pageIndex + 1} of {totalPages}
                </span>

                <button
                    onClick={() => goToPage(pageIndex + 1)}
                    disabled={pageIndex >= totalPages - 1}
                    className="p-2 text-gray-500 bg-white border border-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
};
